import { useGetAllDocumentsQuery, useGetDocumentTypesQuery } from "@api/documentManagement";
import { useDocumentUpload } from "@pages/profile-v2/pages/documents/hooks/useDocumentUpload";
import UploadIcon from "@pages/profile/assets/images/upload.svg";
import UploadAcceptedIcon from "@pages/profile/assets/images/uploadAccepted.svg";
import { Button, Upload } from "antd";
import PropTypes from "prop-types";
import React, { memo, useCallback } from "react";
import styled from "styled-components";
import { BettoCard } from "../../../components/BettoCard";
import { BettoEmptyState } from "../../../components/BettoEmptyState";
import { BTTO_PROFILE_COLORS } from "../../../shared/elements";

const DocItem = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 12px 0;
	border-bottom: 1px solid ${BTTO_PROFILE_COLORS.border};
	flex-wrap: wrap;

	&:last-child {
		border-bottom: none;
	}
`;

const DocInfo = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
	flex: 1;
`;

const DocName = styled.span`
	color: ${BTTO_PROFILE_COLORS.text};
	font-size: 14px;
	font-weight: 500;
`;

const DocStatus = styled.span`
	font-size: 12px;
	font-weight: 500;
	color: ${({ $status }) => {
		switch ($status) {
			case "Accepted":
				return BTTO_PROFILE_COLORS.success;
			case "Rejected":
				return BTTO_PROFILE_COLORS.danger;
			case "Pending":
				return BTTO_PROFILE_COLORS.warning;
			default:
				return BTTO_PROFILE_COLORS.textMuted;
		}
	}};
`;

export const PersonalVerificationSection = memo(({ translate }) => {
	const { data: allDocs, isLoading: documentsLoading } = useGetAllDocumentsQuery();
	const { data: docTypes } = useGetDocumentTypesQuery();
	const { handleBeforeUpload, customUpload, isDocumentUploading } = useDocumentUpload(translate);

	const documents = allDocs?.documents || [];

	const renderUploadButton = useCallback(
		(item) => {
			if (!item || (item.status !== "Rejected" && item.status !== "DocumentWaiting")) {
				return null;
			}
			const isUploading = isDocumentUploading(item.documentName);
			return (
				<Upload
					beforeUpload={handleBeforeUpload}
					customRequest={(e) => customUpload({ file: e.file, record: item })}
					showUploadList={false}
					disabled={isUploading}>
					<Button
						type="default"
						loading={isUploading}
						disabled={isUploading}>
						<img
							src={UploadIcon}
							alt=""
						/>
						{translate("UPLOAD")}
					</Button>
				</Upload>
			);
		},
		[customUpload, translate, handleBeforeUpload, isDocumentUploading]
	);

	return (
		<BettoCard title={translate("betto.profile.documents")}>
			{documentsLoading ? null : documents.length > 0 ? (
				documents.map((item, index) => {
					if (!item) return null;
					const docGroupName = docTypes?.documentGroups?.find(
						(x) => x.documentName === item.documentName
					);
					const docName = docGroupName?.documentGroupName || item.documentName;
					return (
						<DocItem key={`doc-${index}`}>
							<DocInfo>
								<DocName>
									{docName?.indexOf("_") > -1 ? docName.split("_")[0] : docName}
								</DocName>
								<DocStatus $status={item.status}>
									{translate(item.status?.toUpperCase() || "")}
								</DocStatus>
							</DocInfo>
							{renderUploadButton(item)}
							{item.status === "Accepted" && (
								<img
									src={UploadAcceptedIcon}
									alt=""
								/>
							)}
						</DocItem>
					);
				})
			) : (
				<BettoEmptyState title={translate("NoInfoDocument")} />
			)}
		</BettoCard>
	);
});

PersonalVerificationSection.displayName = "PersonalVerificationSection";

PersonalVerificationSection.propTypes = {
	translate: PropTypes.func.isRequired,
};
